import { ITEM_SHEET_FIELDS, ITEM_SHEET_HIDDEN, ITEM_SHEET_READONLY, ITEM_SHEET_REQUIRED, type ItemExportRow, type ItemFormOptions, type ItemImportField } from "@delta/shared";
import { STRINGS_FOR } from "../../i18n";
import { buildWorkbook } from "../../lib/sheet";

// The simplified items sheet (request of 31 Oct 2026; docs/product-import.md), shared by the template download, the
// export of the items list and the import screen's column matching. Sheet 1 holds the rows: the eight columns the user
// fills, in the order of the request, required headings marked with * on a yellow fill, codes and barcodes formatted
// as text, and a drop-down for the item type. The export adds, after them, the cost price for reading only (grey
// heading saying it is not imported; review of 31 Oct 2026, item 1), then three hidden technical columns (item id,
// category id, price includes VAT); the template has neither the cost nor the hidden columns' values. Sheet 2 is the instructions. Sheet 3, hidden, is "delta-settings": the sheet's format and, in an export, whether
// the prices include VAT, so a re-import reads it instead of asking.

export const SETTINGS_SHEET = "delta-settings";
export const SHEET_FORMAT = "delta-items-simple-1";
/** Every column the import recognises in the simplified sheet. */
export const SHEET_COLUMNS: ItemImportField[] = [...ITEM_SHEET_FIELDS, ...ITEM_SHEET_READONLY, ...ITEM_SHEET_HIDDEN];
const TEMPLATE_COLUMNS: ItemImportField[] = [...ITEM_SHEET_FIELDS, ...ITEM_SHEET_HIDDEN];
const TEXT_COLS: ItemImportField[] = ["code", "baseBarcode", "itemId", "categoryRef"];
const tIn = (l: "en" | "ar", k: string) => STRINGS_FOR(l)[k] ?? k;

/** The heading of a column in a language: required ones end with " *"; hidden ones are technical names. */
export function heading(l: "en" | "ar", f: ItemImportField): string {
  if ((ITEM_SHEET_HIDDEN as readonly string[]).includes(f)) return f === "itemId" ? "item_id" : f === "categoryRef" ? "category_id" : "price_includes_vat";
  return tIn(l, `impS_${f}`) + ((ITEM_SHEET_REQUIRED as readonly string[]).includes(f) ? " *" : "");
}
/** Every name a column of the simplified sheet is recognised by, in either language. */
export function headingNames(f: ItemImportField): string[] {
  return [f, heading("en", f), heading("ar", f), tIn("en", `impS_${f}`), tIn("ar", `impS_${f}`)];
}
export const classValue = (l: "en" | "ar", c: "stock" | "nonStock" | null) => (c ? tIn(l, `impClass_${c}`) : "");

function workbook(l: "en" | "ar", cols: ItemImportField[], data: string[][], settings: [string, string][]) {
  const help = [[tIn(l, "impHelpColumn"), tIn(l, "impHelpReq"), tIn(l, "impHelpHow")],
    ...ITEM_SHEET_FIELDS.map((f) => [tIn(l, `impS_${f}`), tIn(l, (ITEM_SHEET_REQUIRED as readonly string[]).includes(f) ? "impReqYes" : "impReqNo"), tIn(l, `impHelp_${f}`)]),
    ...(cols.includes("costPrice") ? [[tIn(l, "impS_costPrice"), tIn(l, "impReadOnly"), tIn(l, "impHelp_costPrice")]] : []),
    [""], [tIn(l, "impHelpNoQty")], [tIn(l, "impHelpNoTax")], [tIn(l, cols.includes("costPrice") ? "impHelpNoCostExport" : "impHelpNoCost")], [tIn(l, "impHelpSample")], [tIn(l, "impHelpHidden")]];
  const idx = (f: ItemImportField) => cols.indexOf(f);
  return buildWorkbook([
    { name: tIn(l, "impSheetItems"), rows: [cols.map((f) => heading(l, f)), ...data], rtl: l === "ar",
      textCols: TEXT_COLS.map(idx), hiddenCols: ITEM_SHEET_HIDDEN.map(idx), requiredCols: ITEM_SHEET_REQUIRED.map(idx), readonlyCols: ITEM_SHEET_READONLY.map(idx).filter((i) => i >= 0),
      lists: [{ col: idx("itemClass"), values: [tIn(l, "impClass_stock"), tIn(l, "impClass_nonStock")] }],
      widths: { [idx("nameAr")]: 30, [idx("nameEn")]: 30, [idx("itemId")]: 38, [idx("categoryRef")]: 38, ...(idx("costPrice") >= 0 ? { [idx("costPrice")]: 30 } : {}) } },
    { name: tIn(l, "impSheetHelp"), rows: help, rtl: l === "ar", widths: { 0: 26, 1: 14, 2: 110 } },
    { name: SETTINGS_SHEET, rows: [["key", "value"], ["format", SHEET_FORMAT], ...settings], hidden: true }
  ]);
}

/** The template: one example row (code SAMPLE-0001, skipped by the import), in a category of the business when it has one. */
export function simpleTemplate(l: "en" | "ar", opts: ItemFormOptions | null): Blob {
  const cat = opts?.categories[0];
  const ex: Partial<Record<ItemImportField, string>> = { code: "SAMPLE-0001", nameAr: tIn("ar", "impSampleCoffee"), nameEn: tIn("en", "impSampleCoffee"),
    category: cat?.nameAr ?? "", categoryEn: cat?.nameEn ?? "", itemClass: tIn(l, "impClass_stock"), priceRetail: "25.00", baseBarcode: "0012345678905" };
  return workbook(l, TEMPLATE_COLUMNS, [TEMPLATE_COLUMNS.map((f) => ex[f] ?? "")], []);
}

/** The export: every item with the same columns, its hidden ids and price treatment, and the file's usual treatment. */
export function exportWorkbook(l: "en" | "ar", rows: ItemExportRow[], pricesIncludeTax: boolean, at: string): Blob {
  const cell = (x: ItemExportRow, f: ItemImportField): string => {
    switch (f) {
      case "itemClass": return classValue(l, x.itemClass);
      case "priceIncludeTax": return x.priceIncludeTax ? "yes" : "no";
      default: return String((x as unknown as Record<string, unknown>)[f] ?? "");
    }
  };
  return workbook(l, SHEET_COLUMNS, rows.map((x) => SHEET_COLUMNS.map((f) => cell(x, f))), [["priceMode", pricesIncludeTax ? "inclusive" : "exclusive"], ["exportedAt", at]]);
}

/** Downloads every item as the simplified sheet (GET /items/export), in the screen's language. */
export async function downloadExport(l: "en" | "ar", api: <T>(m: "GET", url: string) => Promise<T>, save: (b: Blob, name: string) => void): Promise<number> {
  const d = await api<import("@delta/shared").ItemExportDto>("GET", "/items/export");
  save(exportWorkbook(l, d.rows, d.pricesIncludeTax, d.at), `items-${d.at.slice(0, 10)}.xlsx`);
  return d.rows.length;
}
